import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ModelQueryHistoryStatus,
  type MostQueriedResourcesQuery,
  MostQueriedResourceType,
} from '__generated__/graphql';
import { max } from 'd3';

import {
  Accessor,
  Bars,
  Card,
  CategoryAxis,
  categoryScale,
  ChartProps,
  InteractionItem,
  LinearAxis,
  linearScale,
  Link,
  Plot,
  TooltipCallbackArgs,
} from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useMostQueriedResources } from '@/hooks/discovery/performance/mostQueriedResources';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { ArrElement } from '@/typings/arrayElement';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { ChartDateDropdown, DateRange, DEFAULT_DATE_RANGE } from './ChartDateDropdown';
import { ChartWrapper } from './ChartWrapper';
import { QUERY_USAGE_TOOLTIP_NO_DATA } from './ModelQueryUsageBarChart';

const QUERY_USAGE_TOOLTIP_ERROR = (
  <span>
    An error occurred and the data was not able to load. Learn how to troubleshoot
    permissions{' '}
    <Link
      isInternal={false}
      to="https://docs.getdbt.com/docs/collaborate/model-query-history#credential-permissions"
      onClick={(e) => e.stopPropagation()}
      shouldOpenNewTab
    >
      here
    </Link>
    .
  </span>
);

type MostQueriedModel = ArrElement<
  MostQueriedResourcesQuery['performance']['mostQueriedResources']
>;

export const MostQueriedModelsColumnChartImpl = () => {
  const { accountId, projectId, environmentId } = useDbtCloudProject();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);

  const getResourceDetailsPageUrl = useLinkToExplorer('resourceDetails');
  const navigate = useNavigate();

  const projectScope = useUrlProjectParams();

  const [MostQueriedModels] = useMostQueriedResources({
    environmentId,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    resourceTypes: [MostQueriedResourceType.Model],
    limit: 5,
  });

  if (!accountId || !projectId) {
    throw new Error(
      'This component expects to be in a URL with accountId and projectId parameters!',
    );
  }

  const rawData = MostQueriedModels.data?.performance?.mostQueriedResources;
  const oldestQueryHistoryDate =
    MostQueriedModels.data?.performance?.oldestQueryHistoryDate;

  const chartProps: ChartProps | null = useMemo(() => {
    const chartData = rawData ?? [];

    const xAccessor: Accessor<MostQueriedModel, string> = (x) =>
      parseUniqueId(x.uniqueId).remainder;
    const yScale = linearScale({
      min: 0,
      max: max(chartData, (x) => x.totalCount) || 0,
    });
    const xScale = categoryScale({ labels: chartData.map(xAccessor) });

    const chartProps: Partial<ChartProps> = {
      margin: { left: 50, right: 5, top: 20, bottom: 40 },
      yScale,
      xScale,
      xAccessor,
      yAccessor: (x) => x.totalExecutions || 0,
      tooltip: undefined,
      onItemClick(item: InteractionItem<MostQueriedModel>) {
        // todo: Handle deleted nodes in the future
        // const nodeIsDeleted = item?.item.isDeleted;
        // if (nodeIsDeleted) return;

        const url = getResourceDetailsPageUrl({
          ...projectScope,
          dbtUniqueId: item?.item?.uniqueId || '',
          tab: 'performance',
        });
        navigate(url);
      },
    };

    chartProps.yAccessor = (x: MostQueriedModel) => x.totalCount || 0;
    chartProps.tooltip = ({
      interactionItem,
    }: TooltipCallbackArgs<MostQueriedModel>) => {
      if (!interactionItem?.item) {
        return null;
      }
      // const nodeIsDeleted = interactionItem.item.isDeleted;
      const totalCount = interactionItem?.item?.totalCount || 0;
      return (
        <div>
          <div className="text-xs">{totalCount.toLocaleString()} queries</div>
          {/* {nodeIsDeleted && (
            <div className="text-xs">
              <i>This is a deleted model.</i>
            </div>
          )} */}
        </div>
      );
    };

    return chartProps as ChartProps;
  }, [rawData, getResourceDetailsPageUrl, navigate, projectScope]);

  const selectedChartTestId = 'most-queried-models-chart';

  const modelQueryHistoryState =
    MostQueriedModels.data?.environment.modelQueryHistoryState.state;

  const noDataMessage =
    modelQueryHistoryState === ModelQueryHistoryStatus.Success
      ? 'No models were queried in this time period'
      : modelQueryHistoryState === ModelQueryHistoryStatus.NoData
        ? QUERY_USAGE_TOOLTIP_NO_DATA
        : QUERY_USAGE_TOOLTIP_ERROR;

  return (
    <Card>
      <div data-testid="most-queried-models-chart-container">
        <div
          className="mb-5 flex flex-wrap justify-end space-y-2"
          data-testid="most-queried-models-chart-header"
        >
          <div
            className={`ml-[${chartProps?.margin.left}px] flex-1 justify-start whitespace-nowrap text-xl`}
            data-testid="most-queried-models-chart-title"
          >
            Most consumed models
          </div>
          <ChartDateDropdown
            onChange={setDateRange}
            testId="most-queried-models-chart-controls-date-dropdown"
            oldestDate={
              oldestQueryHistoryDate != null
                ? new Date(oldestQueryHistoryDate)
                : undefined
            }
          />
        </div>
        <ChartWrapper
          numDataPoints={
            modelQueryHistoryState === ModelQueryHistoryStatus.Success
              ? rawData?.length
              : 0
          }
          noDataMessage={noDataMessage}
          {...chartProps}
          height={350}
          error={MostQueriedModels.error?.message}
          isLoading={MostQueriedModels.fetching}
          testId={selectedChartTestId}
        >
          <CategoryAxis axis="x" label="Models" />
          <LinearAxis axis="y" label="Consumption query count" />
          <Plot
            data={
              modelQueryHistoryState === ModelQueryHistoryStatus.Success && rawData
                ? rawData
                : []
            }
            data-testid={`${selectedChartTestId}-plot`}
            itemComponent={Bars}
            extraItemProps={{
              className:
                'fill-fgVizIndigo transition-all hover:fill-fgVizIndigoHover hover:cursor-pointer',
            }}
          />
        </ChartWrapper>
      </div>
    </Card>
  );
};

export const MostQueriedModelsColumnChart = () => {
  const { enableModelQueryHistory } = useDbtCloudProject();

  if (!enableModelQueryHistory) {
    return null;
  }

  return <MostQueriedModelsColumnChartImpl />;
};
